import * as yup from 'yup'
import * as _ from './message'
import { passwordRegex } from './helpers'
import { allAssetsClass, publishedStatus } from '../types/opportunityTypes'

const signInSchema = yup.object().shape({
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
})

const forgotPasswordSchema = yup.object({
  password: yup
    .string()
    .required(_.PASSWORD_REQUIRED_ERROR)
    .matches(passwordRegex, _.PASSWORD_ERROR),
  passwordConfirmation: yup
    .string()
    .required(_.CONFIRM_PASSOWRD_REQUIRED_ERROR)
    .oneOf([yup.ref('password'), null], _.CONFIRM_PASSOWRD_ERROR),
})

/** Matches server admin password rules (users/types strongPassword). */
const adminAccountStrongPasswordRegex =
  /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[#?!@$%^&*-]).{8,}$/

const adminAccountChangePasswordSchema = yup.object({
  currentPassword: yup.string().required('Current password is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .matches(
      adminAccountStrongPasswordRegex,
      'Use at least 8 characters with upper, lower, number, and special (#?!@$%^&*-)',
    )
    .test(
      'differs-from-current',
      'New password must differ from your current password',
      function (value) {
        const { currentPassword } = this.parent as { currentPassword?: string }
        return value !== currentPassword
      },
    ),
  newPasswordConfirmation: yup
    .string()
    .required(_.CONFIRM_PASSOWRD_REQUIRED_ERROR)
    .oneOf([yup.ref('newPassword')], _.CONFIRM_PASSOWRD_ERROR),
})
const emailInputSchema = yup.object({
  email: yup.string().required(_.EMAIL_REQUIRED_ERROR).email(_.EMAIL_ERROR),
})

const projectSchema = yup.object({
  projectName: yup.string().max(70).required(_.isRequiredMessage('Opportunity Name')),
  projectSlug: yup.string(),
  valuation: yup.number().positive().min(0),
  dealContours: yup.string(),
  companyMilestones: yup.string(),
  clientEngagement1: yup.string(),
  clientEngagement2: yup.string(),
  clientEngagement3: yup.string(),
  clientEngagement4: yup.string(),
  keyInvestorsImg1: yup.string(),
  keyInvestorsImg2: yup.string(),
  keyInvestorsImg3: yup.string(),
  keyInvestorsImg4: yup.string(),
  keyInvestorsImg5: yup.string(),
  keyInvestorsName1: yup.string(),
  keyInvestorsName2: yup.string(),
  keyInvestorsName3: yup.string(),
  keyInvestorsName4: yup.string(),
  keyInvestorsName5: yup.string(),
  isHighCommitenceProject: yup.boolean(),
  isHideCompanyDetails: yup.boolean(),
  isFeatured: yup.boolean(),
  isPublished: yup.boolean(),
  irr: yup.number().positive().max(100).min(0),
  ticketSize: yup
    .number()
    .min(0)
    .lessThan(yup.ref('raiseGoal'), 'Minimum Amount should be less than Raise Goal'),
  assetClass: yup.string(),
  location: yup.string(),
  raiseGoal: yup
    .number()
    .min(0)
    .lessThan(yup.ref('overallRaiseGoal'), 'Raise Goal should be less than Overall Raise Goal'),
  overallRaiseGoal: yup.number().min(0),
  legalDocuments: yup.string().url(),
  projectSummary: yup.string().max(500),
  closingDate: yup.date(),
  companyName: yup.string(),
  companyDetails: yup.string().max(700),
  companyWebsite: yup.string().url(),
  companyLogo: yup.string().min(30),
  projectCover: yup.string().min(30),
  subscriptionAgreement: yup.string().min(30),
  investmentTimelineStartDate: yup.date().optional(),
  investmentTimelineEndDate: yup.date().optional(),
  shipmentStatus: yup.string(),
  expectedShipmentCompletionDate: yup.date().optional(),
  plannedShipmentCompletionDate: yup.date().optional(),
  productVideoURL: yup.string().optional(),
  productImages: yup.array().of(yup.string()).optional(),
})

const homeSchema = yup.object({
  keyInvestorsImg1: yup.string().optional().nullable(),
  keyInvestorsImg2: yup.string().optional().nullable(),
  keyInvestorsImg3: yup.string().optional().nullable(),
  keyInvestorsImg4: yup.string().optional().nullable(),
  keyInvestorsImg5: yup.string().optional().nullable(),
  keyInvestorsName1: yup.string().optional().nullable(),
  keyInvestorsName2: yup.string().optional().nullable(),
  keyInvestorsName3: yup.string().optional().nullable(),
  keyInvestorsName4: yup.string().optional().nullable(),
  keyInvestorsName5: yup.string().optional().nullable(),
})

const opportunityDocumentSchema = yup.object({
  title: yup.string().optional(),
  url: yup.string().when('title', {
    is: (title: string) => title && title.trim() !== '',
    then: yup.string().required('Document needs to be uploaded when title is filled'),
    otherwise: yup.string().optional(),
  }),
})

const termObjectSchema = yup.object({
  title: yup.string().optional(),
  content: yup.string().optional(),
})

const opportunityTermSchema = yup.object({
  level1: yup
    .object({
      header: yup.string().optional(),
      termObjects: yup
        .array()
        .of(termObjectSchema)
        .test(
          'termObjects-empty-validation',
          'Term objects must have non-empty title and content when header is provided.',
          function (value) {
            const { header } = this.parent
            if (header && header.trim() !== '') {
              const invalidItems = Array.isArray(value)
                ? value?.filter(
                    (item) =>
                      !item.title ||
                      !item.content ||
                      item.title.trim() === '' ||
                      item.content.trim() === '',
                  )
                : []

              return invalidItems.length === 0
            }
            return true
          },
        )
        .test(
          'header-empty-validation',
          'Header must not be empty when title and content are provided',
          function (value) {
            const { header } = this.parent
            if (!header || (header && header.trim() === '')) {
              const invalidItems = Array.isArray(value)
                ? value?.filter(
                    (item) =>
                      (item.title && item.title.trim() !== '') ||
                      (item.content && item.content.trim() !== ''),
                  )
                : []

              return invalidItems.length === 0
            }
            return true
          },
        ),
    })
    .required(),
  level2: yup
    .object({
      header: yup.string().optional(),
      termObjects: yup
        .array()
        .of(termObjectSchema)
        .test(
          'termObjects-empty-validation',
          'Term objects must have non-empty title and content when header is provided.',
          function (value) {
            const { header } = this.parent
            if (header && header.trim() !== '') {
              const invalidItems = Array.isArray(value)
                ? value?.filter(
                    (item) =>
                      !item.title ||
                      !item.content ||
                      item.title.trim() === '' ||
                      item.content.trim() === '',
                  )
                : []

              return invalidItems.length === 0
            }
            return true
          },
        )
        .test(
          'header-empty-validation',
          'Header must not be empty when title and content are provided',
          function (value) {
            const { header } = this.parent
            if (!header || (header && header.trim() === '')) {
              const invalidItems = Array.isArray(value)
                ? value?.filter(
                    (item) =>
                      (item.title && item.title.trim() !== '') ||
                      (item.content && item.content.trim() !== ''),
                  )
                : []

              return invalidItems.length === 0
            }
            return true
          },
        ),
    })
    .required(),
})

const opportunityMetadataSchema = yup.object({
  company: yup.object().optional(),
  request_allocation_enabled: yup.boolean().optional(),
  highlight_enabled: yup.boolean().optional(),
  price_per_share: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .matches(/^\d+(\.\d{1,2})?$/, 'Enter a valid price (up to 2 decimals)')
    .nullable()
    .optional(),
  sales_fee_percent: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .matches(/^\d+(\.\d{1,2})?$/, 'Enter a valid percentage (up to 2 decimals)')
    .nullable()
    .optional(),
  portfolio_service_fee: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  asset_fee: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  trail_commission_fee: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  upfront_commission: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  hurdle_rate: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  carry_rate: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
  underlying_fund: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .trim()
    .nullable()
    .optional(),
  securities_offering: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .trim()
    .nullable()
    .optional(),
  reference_valuation: yup
    .object({
      currency: yup
        .string()
        .transform((v, o) => (o === '' ? undefined : v))
        .oneOf(['USD', 'AED', 'GBP', 'EUR'], 'Currency must be USD, AED, GBP, or EUR')
        .nullable()
        .optional(),
      amount: yup
        .number()
        .transform((v, o) => (o === '' || o === null || o === undefined ? undefined : v))
        .typeError('Reference Valuation Amount must be a number')
        .nullable()
        .optional(),
    })
    .nullable()
    .optional()
    .test(
      'reference-valuation-both-or-neither',
      'Reference valuation requires both currency and amount',
      (value) => {
        if (!value) return true
        const hasCurrency = Boolean(value.currency)
        const hasAmount =
          value.amount !== null && value.amount !== undefined && !Number.isNaN(Number(value.amount))
        if (!hasCurrency && !hasAmount) return true
        if (hasCurrency && hasAmount && Number(value.amount) > 0) return true
        return false
      },
    ),
  termsheet_agreement_template_s3_url: yup
    .string()
    .transform((v, o) => (o === '' ? undefined : v))
    .nullable()
    .optional(),
})

const opportunitySchema = yup.object({
  opportunity_title: yup.string().required(_.isRequiredMessage('Opportunity Name')),
  opportunity_slug: yup.string(),
  asset_class: yup
    .string()
    .transform((value) => (typeof value === 'string' ? value.trim() : value))
    .required(_.isRequiredMessage('Asset Class'))
    .oneOf(allAssetsClass),
  sector_id: yup.number().nullable().required(_.isRequiredMessage('Sector')),
  jurisdiction: yup.string().required(_.isRequiredMessage('Jurisdiction')),
  currency_code: yup
    .string()
    .required(_.isRequiredMessage('Currency Code'))
    .oneOf(['USD', 'AED', 'GBP', 'EUR'], 'Currency Code must be USD, AED, GBP, or EUR'),
  ticket_size: yup.number().required(_.isRequiredMessage('Minimum Amount')),
  closing_date: yup.date().required(_.isRequiredMessage('Closing Date')),
  investment_return: yup.number().optional(),
  raise_goal: yup.number().typeError('Raise Goal must be a number').nullable().optional(),
  opportunity_summary: yup.string().required(_.isRequiredMessage('Opportunity Summary')),
  short_opportunity_summary: yup
    .string()
    .max(255, 'Short summary cannot exceed 255 characters')
    .required(_.isRequiredMessage('Short Opportunity Summary')),
  is_featured: yup.boolean().optional().default(false),
  is_public: yup.boolean().optional().default(true),
  marketing_signup_enabled: yup.boolean().optional().default(false),
  transfer_page_enabled: yup.boolean().optional().default(true),
  secondary_trading_enabled: yup.boolean().optional().default(false),
  marketing_signup_image: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === null ? '' : value))
    .when('marketing_signup_enabled', {
      is: true,
      then: (schema) => schema.required(_.isRequiredMessage('Marketing landing image (desktop)')),
      otherwise: (schema) => schema.optional(),
    }),
  marketing_signup_mobile_image: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === null ? '' : value))
    .optional(),
  metadata: opportunityMetadataSchema.optional(),
  cover_image: yup.string().optional(),
  logo_image: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === null ? '' : value))
    .optional(),
  logo_wide_image: yup
    .string()
    .nullable()
    .transform((value, originalValue) => (originalValue === null ? '' : value))
    .optional(),
  documents: yup.array().of(opportunityDocumentSchema).required(_.isRequiredMessage('Documents')),
  status: yup.string().required(_.isRequiredMessage('Status')).oneOf(publishedStatus),

  // Trade Finance specific fields
  investment_period_in_days: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Investment period must be a number')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Investment period must be a number')
        .required('Investment period is required for Trade Finance and Private Credit'),
      otherwise: yup.number().nullable().optional(),
    }),
  interest_charged_by_credibila_to_opportunity_percent: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Interest charged by Credibila must be a number')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Interest charged by Credibila must be a number')
        .required('Interest charged by Credibila is required for Trade Finance and Private Credit'),
      otherwise: yup.number().nullable().optional(),
    }),
  expected_spread_percent: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Expected spread must be a number')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Expected spread must be a number')
        .required('Expected spread is required for Trade Finance and Private Credit'),
      otherwise: yup.number().nullable().optional(),
    }),
  days_from_withdraw_request_to_payment: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Days from withdraw request to payment must be a number')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Days from withdraw request to payment must be a number')
        .required(
          'Days from withdraw request to payment is required for Trade Finance and Private Credit',
        ),
      otherwise: yup.number().nullable().optional(),
    }),
  exit_load_percent: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Exit load must be a number')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Exit load must be a number')
        .required('Exit load is required for Trade Finance and Private Credit'),
      otherwise: yup.number().nullable().optional(),
    }),

  hosting_fee_percent: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Hosting fee must be a number')
    .optional(),
  has_lock_in_period: yup
    .boolean()
    .typeError('Has lock-in period must be a boolean')
    .when('asset_class', {
      is: (value: any) => value === 'Trade Finance' || value === 'Private Credit',
      then: yup
        .boolean()
        .required('Has lock-in period is required for Trade Finance and Private Credit'),
      otherwise: yup.boolean().nullable().optional(),
    }),
  lock_in_period_in_days: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .when('has_lock_in_period', {
      is: true,
      then: yup.number().required('Lock-in period days is required'),
      otherwise: yup.number().nullable().optional(),
    }),

  // Mutual Fund specific fields
  // Listed Equity specific fields
  // Note: profit_sharing_to_credibila_percent and hosting_fee_percent are shared with Mutual Fund
  // lock_in_period_in_days is shared with Trade Finance and Mutual Fund
  // Additional validation for Listed Equity profit sharing
  profit_sharing_to_credibila_percent: yup
    .number()
    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
    .nullable()
    .typeError('Profit sharing to Credibila must be a number')
    .when('asset_class', {
      is: (value: any) =>
        value === 'Trade Finance' ||
        value === 'Private Credit' ||
        value === 'Mutual Fund' ||
        value === 'Listed Equity',
      then: yup
        .number()
        .transform((value, originalValue) => (originalValue === '' ? undefined : value))
        .nullable()
        .typeError('Profit sharing to Credibila must be a number')
        .required(
          'Profit sharing to Credibila is required for Trade Finance, Private Credit, Mutual Fund, and Listed Equity',
        ),
      otherwise: yup.number().nullable().optional(),
    }),
})

export {
  signInSchema,
  forgotPasswordSchema,
  adminAccountChangePasswordSchema,
  emailInputSchema,
  projectSchema,
  homeSchema,
  opportunitySchema,
}
